// Removed unused action constants - all userCart actions are now handled by XState machine
import type { AppAction } from './types/ActionTypes'

import { prepareItem } from 'lib/checkout/userCart'
import { isFlightItem } from 'lib/checkout/checkoutUtils'
import { isTwoOneWaysItem } from 'components/Pages/CartReviewPage/CartReviewUtils'
import { suppressCartAutoDropdown } from 'components/Pages/UserCartPage/UserCartPreview/cartAutoDropdownCookie'
import { getCartMachineActor } from 'machines/cart/getCartMachineActor'
import { selectEffectiveVisibleCart, selectItemsInActiveVisibleCart, selectItemsInEffectiveVisibleCart } from 'machines/cart/selectors'
import { softDeleteItemsFromMultiCart } from 'api/cart/softDeleteItemsFromMultiCart'

export function addToUserCart(
  items: Array<App.Checkout.AnyItem>,
  skipNotification: boolean = false,
  requestConnectedRooms: boolean = false,
  dedupeAbandonedItems: boolean = false,
): AppAction {
  return () => {
    // Always use XState machine - multi-cart is always enabled
    getCartMachineActor()?.send({
      type: 'add items to active cart',
      items: items.map(prepareItem),
      requestConnectedRooms,
      dedupeAbandonedItems,
      skipNotification,
    })

    // Suppress auto preview dropdown for 1 day after adding items to prevent it from opening immediately
    if (items.length > 0) {
      suppressCartAutoDropdown()
    }

    // If any of the new items are flights, save all other flights for later
    // when adding two one-way flights or when existing two one-way flights are in the cart
    const hasFlightItems = items.some(isFlightItem)
    if (hasFlightItems) {
      const cartMachineActor = getCartMachineActor()
      if (cartMachineActor) {
        const snapshot = cartMachineActor.getSnapshot()
        const existingCartItems = selectItemsInActiveVisibleCart(snapshot)
        const hasTwoOneWaysFlightItem = items.some(item => isFlightItem(item) && isTwoOneWaysItem(item))

        const existingFlightItems = existingCartItems
          .map(multiCartItem => multiCartItem.item)
          .filter(isFlightItem)
          .filter(item => !items.some(newItem => newItem.itemId === item.itemId))

        existingFlightItems.forEach(item => {
          const multiCartItem = existingCartItems.find(mci => mci.item.itemId === item.itemId)
          const isSaveForLater = multiCartItem?.state === 'save_for_later'
          if (!isSaveForLater && (isTwoOneWaysItem(item) || hasTwoOneWaysFlightItem)) {
            // Use machine to save for later
            cartMachineActor.send({
              type: 'update cart items save for later',
              itemIds: [item.itemId],
              saveForLater: true,
            })
          }
        })
      }
    }
  }
}

/**
 * Removes items from the user cart (XState + server) by resolving checkout itemIds
 * to multi-cart entry IDs. This keeps the user cart in sync when items are removed
 * during checkout.
 */
export function removeFromUserCart(itemIds: Array<string>): AppAction {
  return () => {
    if (itemIds.length === 0) return

    const cartMachineActor = getCartMachineActor()
    if (!cartMachineActor) return

    const snapshot = cartMachineActor.getSnapshot()
    const effectiveVisibleCart = selectEffectiveVisibleCart(snapshot)
    if (!effectiveVisibleCart) return

    const activeCartItems = selectItemsInEffectiveVisibleCart(snapshot)

    const checkoutItemIdToCartEntryId = new Map(
      activeCartItems.map(item => [item.item.itemId, item.id]),
    )
    const cartEntryIds = new Set(activeCartItems.map(item => item.id))

    // get all active cart item id and find their corresponding entry/server id
    const resolvedEntryIds = Array.from(new Set(
      itemIds.flatMap(itemId => {
        if (cartEntryIds.has(itemId)) return [itemId]
        const entryId = checkoutItemIdToCartEntryId.get(itemId)
        return entryId ? [entryId] : []
      }),
    ))

    if (resolvedEntryIds.length === 0) return

    // Persist deletion to user cart DB
    softDeleteItemsFromMultiCart(effectiveVisibleCart.cartId, resolvedEntryIds)

    cartMachineActor.send({
      type: 'remove item from cart',
      cartId: effectiveVisibleCart.cartId,
      itemIds: resolvedEntryIds,
    })
  }
}
